'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Command } from 'cmdk'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { apiClient } from '@/lib/api-client'
import { usePersona } from '@/lib/persona'
import { visibleNavSections, type NavItem } from '@/lib/navigation'
import { NavItemLabel } from '@/components/layout/NavItemLabel'
import type { SearchRow } from '@/lib/dr-types'

/** Window event the header search button sends to open the palette. */
export const OPEN_PALETTE_EVENT = 'aio:open-palette'

export function openCommandPalette(): void {
  window.dispatchEvent(new Event(OPEN_PALETTE_EVENT))
}

/** Records are searched on the server from this many characters (plans/03 A5.3). */
const MIN_QUERY = 2
const DEBOUNCE_MS = 200

const ITEM_CLASS = cn(
  'flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5',
  'text-sm text-foreground',
  'aria-selected:bg-primary aria-selected:text-primary-foreground',
  'transition-colors'
)

/** A page matches on its label, its Japanese label, its keywords ("console", "home") or its section's title. */
function pageMatches(item: NavItem, sectionTitle: string, term: string): boolean {
  if (!term) return true
  const words = [item.label, item.labelJa ?? '', sectionTitle, ...(item.keywords ?? [])]
  return words.some((word) => word.toLowerCase().includes(term))
}

const GROUP_CLASS =
  'px-2 py-1.5 text-xs font-semibold text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pb-1'

/** Server search for records the story clock has reached (`GET /api/dr/search`). */
function useRecordSearch(query: string): { rows: SearchRow[]; searching: boolean; failed: boolean } {
  const [rows, setRows] = React.useState<SearchRow[]>([])
  const [searching, setSearching] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    const q = query.trim()
    if (q.length < MIN_QUERY) {
      setRows([])
      setSearching(false)
      setFailed(false)
      return
    }
    let cancelled = false
    setSearching(true)
    const timer = setTimeout(() => {
      apiClient
        .get<{ rows?: SearchRow[] } | SearchRow[]>(`/api/dr/search?q=${encodeURIComponent(q)}&limit=20`)
        .then((payload) => {
          if (cancelled) return
          setFailed(false)
          setRows(Array.isArray(payload) ? payload : (payload?.rows ?? []))
        })
        .catch(() => {
          if (cancelled) return
          setRows([])
          setFailed(true)
        })
        .finally(() => {
          if (!cancelled) setSearching(false)
        })
    }, DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [query])

  return { rows, searching, failed }
}

/**
 * Ctrl K / Cmd K (plans/03 A5.3): the pages this persona can open, then
 * records from the server search. Arrow keys move, Enter opens, Escape closes.
 */
export function CommandPalette() {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState('')
  const router = useRouter()
  const { persona } = usePersona()
  const sections = React.useMemo(() => visibleNavSections(persona), [persona])
  const { rows, searching, failed } = useRecordSearch(open ? search : '')

  React.useEffect(() => {
    const down = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setOpen((value) => !value)
      }
    }
    document.addEventListener('keydown', down)
    return () => document.removeEventListener('keydown', down)
  }, [])

  React.useEffect(() => {
    const onOpen = () => setOpen(true)
    window.addEventListener(OPEN_PALETTE_EVENT, onOpen)
    return () => window.removeEventListener(OPEN_PALETTE_EVENT, onOpen)
  }, [])

  React.useEffect(() => {
    if (!open) setSearch('')
  }, [open])

  const go = (href: string) => {
    setOpen(false)
    router.push(href)
  }

  const term = search.trim().toLowerCase()
  const pageSections = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => pageMatches(item, section.title, term)),
    }))
    .filter((section) => section.items.length > 0)
  const nothing = pageSections.length === 0 && rows.length === 0 && !searching

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className='overflow-hidden p-0 sm:max-w-xl [&>button]:hidden'>
        <DialogTitle className='sr-only'>Search</DialogTitle>
        <DialogDescription className='sr-only'>
          Search the pages you can open and records such as Workbench items, places, deliveries and Autos.
        </DialogDescription>
        <Command className='overflow-hidden rounded-xl bg-card' loop shouldFilter={false} label='Search'>
          <div className='flex items-center border-b border-border px-4'>
            <Icons.search className='h-4 w-4 shrink-0 text-muted-foreground' aria-hidden='true' />
            <Command.Input
              autoFocus
              value={search}
              onValueChange={setSearch}
              placeholder='Search pages, Workbench items, places, Autos'
              className='h-14 flex-1 bg-transparent px-3 text-foreground placeholder:text-muted-foreground focus:outline-none'
            />
            <kbd className='hidden h-6 items-center rounded bg-muted px-2 font-mono text-xs text-muted-foreground sm:inline-flex'>
              Esc
            </kbd>
          </div>

          <Command.List className='max-h-[360px] overflow-y-auto p-2'>
            <div role='status' className={cn(!nothing && !failed && 'sr-only')}>
              {failed ? (
                <p className='px-3 py-2 text-sm text-muted-foreground'>Record search is not available right now.</p>
              ) : nothing ? (
                <p className='py-6 text-center text-sm text-muted-foreground'>No match</p>
              ) : searching ? (
                'Searching records'
              ) : null}
            </div>

            {pageSections.length > 0 && (
              <Command.Group heading='Go to' className={GROUP_CLASS}>
                {pageSections.flatMap((section) =>
                  section.items.map((item) => (
                    <Command.Item key={item.id} value={`page:${item.id}`} onSelect={() => go(item.href)} className={ITEM_CLASS}>
                      <item.icon className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
                      <span className='flex-1'>
                        <NavItemLabel item={item} />
                      </span>
                      <span className='text-xs opacity-70'>{section.title}</span>
                    </Command.Item>
                  ))
                )}
              </Command.Group>
            )}

            {(rows.length > 0 || searching) && (
              <Command.Group heading='Records' className={GROUP_CLASS}>
                {searching && rows.length === 0 && (
                  <Command.Loading>
                    <div className='px-3 py-2 text-sm text-muted-foreground' aria-hidden='true'>
                      Searching
                    </div>
                  </Command.Loading>
                )}
                {rows.map((row) => (
                  <Command.Item
                    key={`${row.kind}:${row.code}`}
                    value={`record:${row.kind}:${row.code}`}
                    onSelect={() => go(row.href)}
                    className={ITEM_CLASS}
                  >
                    <Icons.arrowRight className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
                    <span className='min-w-0 flex-1 truncate'>
                      <span className='font-mono text-xs font-semibold'>{row.code}</span>
                      <span> · {row.title}</span>
                    </span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}
          </Command.List>

          <div className='flex items-center justify-between border-t border-border px-4 py-2 text-xs text-muted-foreground'>
            <div className='flex items-center gap-4'>
              <span className='flex items-center gap-1'>
                <kbd className='rounded bg-muted px-1'>↑↓</kbd>
                Move
              </span>
              <span className='flex items-center gap-1'>
                <kbd className='rounded bg-muted px-1'>↵</kbd>
                Open
              </span>
            </div>
            <span className='flex items-center gap-1'>
              <kbd className='rounded bg-muted px-1'>Esc</kbd>
              Close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  )
}
